//deps
import { useContext, useState, useCallback } from "react";

//apis
import { addMoney } from "@payment-client/apis/Wallet";
import { addMoneyPublic } from "@payment-client/apis/publicWallet";

//helpers
import { showNativeAlert, showNativeHeader } from "@payment-client/utils/Native";
import { getAddMoneyRedirectUrl } from "@payment-client/helpers/PaymentHelper";
import { createAndSubmitForm } from "@payment-client/utils/Form";
import { saveAddMoneySelectedWallet } from "@payment-client/helpers/CheckoutStateHelper";

//constants
import { PAYMENT_BACK_ALERT_OPTIONS } from "@payment-client/constants/TextDict";

//internal-deps
import {
    ConfigContext,
    AnalyticsContext,
    ApiContext,
    CheckoutActionTypes,
    CheckoutContext,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    getBridgeSdk,
    PublicApiContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
} from "@swiggy-private/checkout-core";
import { captureError, GlobalStoreContext, GlobalStoreContextType, isEmpty } from "@swiggy-private/core-ui";

//analytics
import { onAddMoneyClickEvent } from "@payment-client/analytics/events/Payment";
import { SCREEN_NAME } from "@payment-client/analytics/constants";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

type AddMoneyFn = (data: {
    walletCode: string;
    mobile: string;
    amount: string;
    isPaymentModeFlow?: boolean;
}) => Promise<void>;

export const useAddMoney = (): [boolean, AddMoneyFn] => {
    const apiContextValue = useContext(ApiContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { addMoneyReturnUrl } = useContext(ConfigContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const globalStore = useContext<GlobalStoreContextType>(GlobalStoreContext);
    const [addMoneyInProgress, setAddMoneyInProgress] = useState(false);
    const apiHost = apiContextValue.host.unwrap();
    const publicApiConfig = useContext(PublicApiContext);

    const addMoneyToWallet = useCallback(
        async ({ mobile: cell, ...data }: Parameters<AddMoneyFn>[0]) => {
            setAddMoneyInProgress(true);
            const screenName = data.isPaymentModeFlow ? SCREEN_NAME.ADD_MONEY : SCREEN_NAME.PAYMENT;
            onAddMoneyClickEvent(data.amount, data.walletCode, clickEvent, screenName);

            // persist the selected cart address id before opening add money webview
            if (!isEmpty(GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartAddressId))) {
                globalStore.set(
                    SwgyHeaderKeysList.cartAddressId,
                    GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartAddressId),
                );
            }

            const isPublicApiEnable = isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.WALLET_APIS,
                data.walletCode.toLowerCase(),
            );

            const addMoneyFunction = isPublicApiEnable ? addMoneyPublic : addMoney;

            try {
                const response = await addMoneyFunction(apiHost, {
                    ...data,
                    cell,
                    addMoneyReturnUrl: addMoneyReturnUrl?.unwrapOr(""),
                });
                const backURL = getAddMoneyRedirectUrl(
                    !!data.isPaymentModeFlow,
                    data.walletCode,
                    addMoneyReturnUrl?.unwrapOr(""),
                );

                getBridgeSdk()
                    .setScreenCaptureState({ screenCapturingDisabled: true })
                    .catch(() => void 0);

                createAndSubmitForm(response);
                showNativeHeader({
                    title: "MAKE PAYMENT",
                    backURL,
                    backAlert: PAYMENT_BACK_ALERT_OPTIONS,
                });
                setCheckoutState({ type: CheckoutActionTypes.SHOW_FULL_PAGE_LOADER });
                if (!data.isPaymentModeFlow) {
                    saveAddMoneySelectedWallet(data.walletCode);
                }
            } catch (err) {
                getBridgeSdk()
                    .setScreenCaptureState({ screenCapturingDisabled: false })
                    .catch(() => void 0);

                captureError(err);
                showNativeAlert();
            }

            setAddMoneyInProgress(false);
        },
        [clickEvent, globalStore, apiHost, addMoneyReturnUrl, setCheckoutState, publicApiConfig],
    );

    return [addMoneyInProgress, addMoneyToWallet];
};
